import type { Question, QuotaGroup } from '@api/surveys/utilities/types';

/**
 * Checks if an item is valid based on a given list of items.
 * An item is considered valid if it matches the ID of any item in the list.
 */
export const checkIfValid = (items: Item[], id?: string | number) => {
  if (id === undefined || id === null) return false;
  return items.some((item) => String(item.id) === String(id));
};

/**
 * Transforms a list of questions or quota groups into a format suitable for dropdown options,
 * where each option has a 'label' (the display text) and a 'value' (the underlying ID).
 */
export const getDropdownOptions = (items: Item[]) => {
  return items.map((item) => {
    const isQuotaGroup = 'name' in item;
    return {
      label: isQuotaGroup ? item.name : item.text,
      value: item.id,
    };
  });
};

/**
 * Constructs the URL for previewing the survey based on the provided preview session ID.
 * Also passes the quota group and question IDs as query parameters if they are provided.
 */
export const buildPreviewUrl = ({
  previewSelection,
  previewSessionId,
}: {
  previewSelection: PreviewSelection;
  previewSessionId: string | null;
}) => {
  const previewEndpoint = import.meta.env.VITE_SURVEY_PREVIEW_ENDPOINT;
  if (!previewEndpoint) return '';

  const previewEndpointUrl = new URL(previewEndpoint);
  if (previewSessionId) {
    previewEndpointUrl.searchParams.set('preview_session_id', previewSessionId);
  }
  if (previewSelection?.quotaGroupId) {
    previewEndpointUrl.searchParams.set('quota_group_id', String(previewSelection.quotaGroupId));
  }
  if (previewSelection?.questionId) {
    previewEndpointUrl.searchParams.set('question_id', String(previewSelection.questionId));
  }
  return previewEndpointUrl.toString();
};

export const getPreviewSelection = ({ questions, quotaGroups }: PreviewSelectionList) => {
  const defaultQuestionId = questions?.[0]?.id;
  const defaultQuotaGroupId = quotaGroups?.[0]?.id;
  return {
    questionId: defaultQuestionId,
    quotaGroupId: defaultQuotaGroupId,
  };
};

export type PreviewSelectionList = {
  questions: Question[];
  quotaGroups: QuotaGroup[];
};

type Item = Question | QuotaGroup;

export interface PreviewSelection {
  quotaGroupId?: string | number;
  questionId?: number;
}
